import React, { useState, useEffect, useCallback } from 'react';
import {
  FiGift, FiPlus, FiSearch, FiEdit2, FiTrash2,
  FiChevronDown, FiChevronUp, FiX, FiAward, FiTarget,
  FiUsers, FiCalendar, FiStar, FiTrendingUp, FiClock,
  FiImage, FiCopy, FiFilter, FiGlobe
} from 'react-icons/fi';
import { rewardsAPI, challengesAPI, eventsAPI } from '../services/api';
import api from '../services/api';
import LanguageTabsEditor from '../components/LanguageTabsEditor';
import toast from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';

// ─── Constants ───────────────────────────────────────────────

const REWARD_TYPES = [
  { value: 'badge', label: 'Badge', icon: FiAward, color: 'text-purple-600' },
  { value: 'nft', label: 'NFT', icon: FiStar, color: 'text-amber-600' },
  { value: 'points', label: 'Bonus Points', icon: FiTrendingUp, color: 'text-green-600' },
  { value: 'unlock', label: 'Feature Unlock', icon: FiGift, color: 'text-blue-600' },
];

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy', color: 'bg-green-100 text-green-800' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'hard', label: 'Hard', color: 'bg-orange-100 text-orange-800' },
  { value: 'legendary', label: 'Legendary', color: 'bg-purple-100 text-purple-800' },
];

const CONDITION_TYPES = [
  { value: 'challenge_complete', label: 'Complete Specific Challenge', category: 'challenge' },
  { value: 'challenges_all', label: 'Complete All Listed Challenges', category: 'challenge' },
  { value: 'challenges_any', label: 'Complete X from List', category: 'challenge' },
  { value: 'challenges_by_type', label: 'Complete X of Type', category: 'challenge' },
  { value: 'challenges_all_type', label: 'Complete ALL of Type', category: 'challenge' },
  { value: 'challenges_count', label: 'Complete X Total Challenges', category: 'challenge' },
  { value: 'challenges_in_period', label: 'Complete X in Period', category: 'time' },
  { value: 'streak', label: 'Consecutive Days Streak', category: 'time' },
  { value: 'date_range', label: 'Available During Dates', category: 'time' },
  { value: 'first_x_users', label: 'First X Users', category: 'limit' },
  { value: 'points_total', label: 'Reach X Points', category: 'progress' },
  { value: 'level', label: 'Reach Level X', category: 'progress' },
  { value: 'event_complete', label: 'Complete Event Challenge', category: 'event' },
  { value: 'events_count', label: 'Complete X Events', category: 'event' },
];

const CHALLENGE_TYPES = [
  'adventure', 'historic', 'museum', 'nature', 'park', 'urban', 'water', 'skiing', 'cultural', 'food'
];

const getRewardTypeInfo = (type) => REWARD_TYPES.find(t => t.value === type) || REWARD_TYPES[0];
const getDifficultyInfo = (diff) => DIFFICULTY_OPTIONS.find(d => d.value === diff);

// ─── Main Component ──────────────────────────────────────────

const Rewards = () => {
  const { t } = useTranslation();
  const [rewards, setRewards] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', type: '', search: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);

  const loadRewards = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.type) params.type = filters.type;
      if (filters.search) params.search = filters.search;

      const [rewardsRes, statsRes] = await Promise.all([
        rewardsAPI.getRewards(params),
        rewardsAPI.getRewardStats()
      ]);
      setRewards(rewardsRes.data.rewards || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading rewards:', error);
      toast.error('Failed to load rewards');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadRewards(); }, [loadRewards]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await rewardsAPI.deleteReward(id);
      toast.success('Reward deleted');
      loadRewards();
    } catch (error) {
      toast.error('Failed to delete reward');
    }
  };

  const openCreate = () => {
    setEditingReward(null);
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await rewardsAPI.getRewardById(id);
      setEditingReward(res.data);
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load reward');
    }
  };

  const duplicateReward = async (reward) => {
    try {
      // Parse existing translations
      const existingTranslations = typeof reward.translations === 'string'
        ? JSON.parse(reward.translations || '{}')
        : (reward.translations || {});

      // Update English name in translations too
      const updatedTranslations = {
        ...existingTranslations,
        en: {
          ...existingTranslations.en,
          name: `${existingTranslations?.en?.name || reward.name} (Copy)`
        }
      };

      const newReward = {
        ...reward,
        name: `${reward.name} (Copy)`,
        isActive: false,
        translations: updatedTranslations
      };
      delete newReward.id;
      delete newReward.createdAt;
      delete newReward.updatedAt;
      delete newReward.claimCount;
      delete newReward.usersEligible;

      await rewardsAPI.createReward(newReward);
      toast.success('Reward duplicated');
      loadRewards();
    } catch (error) {
      toast.error('Failed to duplicate reward');
    }
  };

  // ─── Stats Bar ─────────────────────────────────────────────

  const StatsBar = () => (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      {[
        { label: 'Total Rewards', value: stats?.total || 0, color: 'text-primary-600' },
        { label: 'Active', value: stats?.active || 0, color: 'text-green-600' },
        { label: 'NFT Rewards', value: stats?.nftRewards || 0, color: 'text-amber-600' },
        { label: 'Total Claims', value: stats?.totalClaims || 0, color: 'text-blue-600' },
        { label: 'Pending Claims', value: stats?.pendingClaims || 0, color: 'text-purple-600' }
      ].map(s => (
        <div key={s.label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-500">{s.label}</p>
          <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
        </div>
      ))}
    </div>
  );

  // ─── Reward Card ────────────────────────────────────────────

  const RewardCard = ({ reward }) => {
    const typeInfo = getRewardTypeInfo(reward.rewardType);
    const diffInfo = getDifficultyInfo(reward.difficulty);
    const TypeIcon = typeInfo.icon;

    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow ${!reward.isActive ? 'opacity-60' : ''}`}>
        {/* Header with image or gradient */}
        <div className="h-32 bg-gradient-to-br from-primary-100 to-primary-50 relative flex items-center justify-center">
          {reward.imageUrl ? (
            <img src={reward.imageUrl} alt={reward.name} className="w-full h-full object-cover" />
          ) : (
            <TypeIcon className={`w-12 h-12 ${typeInfo.color}`} />
          )}
          {/* Status badges */}
          <div className="absolute top-2 right-2 flex gap-1">
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
              reward.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {reward.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          {/* Type badge */}
          <span className={`absolute top-2 left-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-white/90 ${typeInfo.color}`}>
            {typeInfo.label}
          </span>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 truncate">{reward.name}</h3>
          {reward.shortDescription && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{reward.shortDescription}</p>
          )}

          {/* Badges */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {diffInfo && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${diffInfo.color}`}>
                {diffInfo.label}
              </span>
            )}
            {reward.category && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                {reward.category}
              </span>
            )}
            {reward.conditions?.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                {reward.conditions.length} condition{reward.conditions.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 text-sm text-gray-500 mt-3">
            <div className="flex items-center gap-1">
              <FiUsers className="w-3.5 h-3.5" />
              <span>{reward.claimCount || 0} claims</span>
            </div>
            {reward.pointsValue > 0 && (
              <div className="flex items-center gap-1">
                <FiTrendingUp className="w-3.5 h-3.5" />
                <span>+{reward.pointsValue} pts</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <button onClick={() => openEdit(reward.id)}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded transition-colors">
              <FiEdit2 className="w-3.5 h-3.5" /> Edit
            </button>
            <button onClick={() => duplicateReward(reward)}
              className="flex items-center justify-center px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-50 rounded transition-colors"
              title="Duplicate">
              <FiCopy className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => handleDelete(reward.id, reward.name)}
              className="flex items-center justify-center px-2 py-1.5 text-sm text-red-500 hover:bg-red-50 rounded transition-colors"
              title="Delete">
              <FiTrash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600 flex items-center gap-2">
            <FiGift className="w-6 h-6" /> {t('cms.nav.rewards')}
          </h1>
          <p className="text-gray-600">{t('cms.rewards.subtitle')}</p>
        </div>
        <button onClick={openCreate}
          className="btn btn-primary flex items-center gap-2">
          <FiPlus className="w-4 h-4" /> {t('cms.rewards.addReward')}
        </button>
      </div>

      {/* Stats */}
      {stats && <StatsBar />}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search rewards..."
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <select
            value={filters.type}
            onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Types</option>
            {REWARD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div className="flex gap-1">
            {[{ value: '', label: 'All' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }].map(s => (
              <button key={s.value}
                onClick={() => setFilters(f => ({ ...f, status: s.value }))}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filters.status === s.value
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Rewards Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : rewards.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FiGift className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No rewards yet</h3>
          <p className="text-gray-500 mt-1">Create your first reward to motivate users.</p>
          <button onClick={openCreate} className="btn btn-primary mt-4">
            <FiPlus className="w-4 h-4 mr-2 inline" /> {t('cms.rewards.addReward')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rewards.map(reward => <RewardCard key={reward.id} reward={reward} />)}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <RewardModal
          reward={editingReward}
          onClose={() => { setShowModal(false); setEditingReward(null); }}
          onSaved={() => { setShowModal(false); setEditingReward(null); loadRewards(); }}
        />
      )}
    </div>
  );
};

// ─── Reward Create/Edit Modal ─────────────────────────────────

const RewardModal = ({ reward, onClose, onSaved }) => {
  const { t } = useTranslation();
  const isEdit = !!reward;
  const [form, setForm] = useState({
    name: '', description: '', shortDescription: '', imageUrl: '', icon: '',
    rewardType: 'badge', nftTemplateId: '', pointsValue: 0, badgeKey: '',
    isActive: true, startDate: '', endDate: '', maxClaims: '', maxPerUser: 1,
    autoClaim: false, showProgress: true, isSecret: false,
    sortOrder: 0, category: '', difficulty: ''
  });
  const [conditions, setConditions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true, conditions: true, settings: false, limits: false, translations: false
  });
  const [challenges, setChallenges] = useState([]);
  const [events, setEvents] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState({ en: { name: '', description: '', shortDescription: '' } });

  useEffect(() => {
    if (reward) {
      setForm({
        name: reward.name || '',
        description: reward.description || '',
        shortDescription: reward.shortDescription || '',
        imageUrl: reward.imageUrl || '',
        icon: reward.icon || '',
        rewardType: reward.rewardType || 'badge',
        nftTemplateId: reward.nftTemplateId || '',
        pointsValue: reward.pointsValue || 0,
        badgeKey: reward.badgeKey || '',
        isActive: reward.isActive !== false,
        startDate: reward.startDate ? new Date(reward.startDate).toISOString().slice(0, 16) : '',
        endDate: reward.endDate ? new Date(reward.endDate).toISOString().slice(0, 16) : '',
        maxClaims: reward.maxClaims || '',
        maxPerUser: reward.maxPerUser || 1,
        autoClaim: reward.autoClaim || false,
        showProgress: reward.showProgress !== false,
        isSecret: reward.isSecret || false,
        sortOrder: reward.sortOrder || 0,
        category: reward.category || '',
        difficulty: reward.difficulty || ''
      });
      setConditions(reward.conditions || []);
    }
    loadChallengesAndEvents();
    loadLanguages();
  }, [reward]);

  // Fetch available languages
  const loadLanguages = async () => {
    try {
      const res = await api.get('/app-config');
      const langs = res.data?.languages || [];
      setLanguages(langs.filter(l => l.enabled));

      // Initialize translations structure for all languages
      const initialTranslations = {};
      langs.filter(l => l.enabled).forEach(l => {
        initialTranslations[l.code] = { name: '', description: '', shortDescription: '' };
      });
      // Ensure English is always there
      if (!initialTranslations.en) {
        initialTranslations.en = { name: '', description: '', shortDescription: '' };
      }
      setTranslations(initialTranslations);
    } catch (e) {
      console.warn('Failed to load languages:', e);
      // Fallback to English only
      setLanguages([{ code: 'en', name: 'English', nativeName: 'English' }]);
    }
  };

  // Load existing translations when editing
  useEffect(() => {
    if (reward) {
      const existingTranslations = typeof reward.translations === 'string'
        ? JSON.parse(reward.translations || '{}')
        : (reward.translations || {});

      setTranslations(prev => ({
        ...prev,
        ...existingTranslations,
        en: {
          name: reward.name || existingTranslations?.en?.name || '',
          description: reward.description || existingTranslations?.en?.description || '',
          short_description: reward.shortDescription || existingTranslations?.en?.shortDescription || ''
        }
      }));
    }
  }, [reward]);

  const loadChallengesAndEvents = async () => {
    try {
      const [challengesRes, eventsRes] = await Promise.all([
        challengesAPI.getChallenges({ limit: 500 }),
        eventsAPI.getEvents({ limit: 200 })
      ]);
      setChallenges(challengesRes.data.challenges || challengesRes.data || []);
      setEvents(eventsRes.data.events || []);
    } catch (e) { /* ignore */ }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    // Sync translatable fields to English translations
    if (['name', 'description', 'shortDescription'].includes(field)) {
      setTranslations(prev => ({
        ...prev,
        en: { ...prev.en, [field]: value }
      }));
    }
  };

  const addCondition = () => {
    setConditions([...conditions, {
      conditionType: 'challenge_complete',
      conditionData: {},
      conditionGroup: 0,
      description: ''
    }]);
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...conditions];
    if (field === 'conditionData') {
      newConditions[index].conditionData = value;
    } else {
      newConditions[index][field] = value;
    }
    setConditions(newConditions);
  };

  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      // Filter out empty translations (keep only locales with actual content)
      const filteredTranslations = {};
      for (const [locale, content] of Object.entries(translations)) {
        if (content && (content.name || content.description || content.shortDescription)) {
          filteredTranslations[locale] = content;
        }
      }

      const payload = {
        ...form,
        pointsValue: parseInt(form.pointsValue) || 0,
        maxClaims: form.maxClaims ? parseInt(form.maxClaims) : null,
        maxPerUser: parseInt(form.maxPerUser) || 1,
        sortOrder: parseInt(form.sortOrder) || 0,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        translations: Object.keys(filteredTranslations).length > 0 ? filteredTranslations : null,
        conditions: conditions.map(c => ({
          ...c,
          conditionData: typeof c.conditionData === 'string' ? JSON.parse(c.conditionData) : c.conditionData
        }))
      };

      if (isEdit) {
        await rewardsAPI.updateReward(reward.id, payload);
        toast.success('Reward updated');
      } else {
        await rewardsAPI.createReward(payload);
        toast.success('Reward created');
      }
      onSaved();
    } catch (error) {
      console.error('Error saving reward:', error);
      toast.error('Failed to save reward');
    } finally {
      setSaving(false);
    }
  };

  // Section component
  const Section = ({ id, title, children }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between px-4 py-3 text-left font-medium text-gray-700 hover:bg-gray-50"
      >
        {title}
        {expandedSections[id] ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
      </button>
      {expandedSections[id] && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );

  // Condition Editor
  const ConditionEditor = ({ condition, index }) => {
    const condType = CONDITION_TYPES.find(t => t.value === condition.conditionType);
    const data = typeof condition.conditionData === 'string'
      ? JSON.parse(condition.conditionData || '{}')
      : (condition.conditionData || {});

    const updateData = (key, value) => {
      updateCondition(index, 'conditionData', { ...data, [key]: value });
    };

    return (
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Condition {index + 1}</span>
          <button onClick={() => removeCondition(index)}
            className="text-red-500 hover:text-red-700">
            <FiX className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
            <select
              value={condition.conditionType}
              onChange={e => {
                updateCondition(index, 'conditionType', e.target.value);
                updateCondition(index, 'conditionData', {});
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {CONDITION_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Group (OR logic)</label>
            <input
              type="number"
              value={condition.conditionGroup || 0}
              onChange={e => updateCondition(index, 'conditionGroup', parseInt(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              min="0"
            />
          </div>
        </div>

        {/* Condition-specific fields */}
        {condition.conditionType === 'challenge_complete' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Challenge</label>
            <select
              value={data.challengeId || ''}
              onChange={e => updateData('challengeId', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select challenge...</option>
              {challenges.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        {(condition.conditionType === 'challenges_all' || condition.conditionType === 'challenges_any') && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Challenges (hold Ctrl to multi-select)</label>
              <select
                multiple
                value={data.challengeIds || []}
                onChange={e => updateData('challengeIds', Array.from(e.target.selectedOptions, o => o.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-32"
              >
                {challenges.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            {condition.conditionType === 'challenges_any' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Required Count</label>
                <input
                  type="number"
                  value={data.requiredCount || 1}
                  onChange={e => updateData('requiredCount', parseInt(e.target.value) || 1)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  min="1"
                />
              </div>
            )}
          </>
        )}

        {(condition.conditionType === 'challenges_by_type' || condition.conditionType === 'challenges_all_type') && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Challenge Type</label>
              <select
                value={data.challengeType || ''}
                onChange={e => updateData('challengeType', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select type...</option>
                {CHALLENGE_TYPES.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
            {condition.conditionType === 'challenges_by_type' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Required Count</label>
                <input
                  type="number"
                  value={data.requiredCount || 1}
                  onChange={e => updateData('requiredCount', parseInt(e.target.value) || 1)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  min="1"
                />
              </div>
            )}
          </>
        )}

        {condition.conditionType === 'challenges_count' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Required Total Challenges</label>
            <input
              type="number"
              value={data.requiredCount || 1}
              onChange={e => updateData('requiredCount', parseInt(e.target.value) || 1)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              min="1"
            />
          </div>
        )}

        {condition.conditionType === 'challenges_in_period' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Period</label>
              <select
                value={data.period || 'week'}
                onChange={e => updateData('period', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Required Count</label>
              <input
                type="number"
                value={data.requiredCount || 1}
                onChange={e => updateData('requiredCount', parseInt(e.target.value) || 1)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                min="1"
              />
            </div>
          </div>
        )}

        {condition.conditionType === 'streak' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Consecutive Days</label>
            <input
              type="number"
              value={data.days || 7}
              onChange={e => updateData('days', parseInt(e.target.value) || 7)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              min="1"
            />
          </div>
        )}

        {condition.conditionType === 'date_range' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
              <input
                type="date"
                value={data.startDate || ''}
                onChange={e => updateData('startDate', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
              <input
                type="date"
                value={data.endDate || ''}
                onChange={e => updateData('endDate', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}

        {condition.conditionType === 'first_x_users' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Max Users (Early Bird)</label>
            <input
              type="number"
              value={data.maxUsers || 10}
              onChange={e => updateData('maxUsers', parseInt(e.target.value) || 10)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              min="1"
            />
          </div>
        )}

        {condition.conditionType === 'points_total' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Minimum Points</label>
            <input
              type="number"
              value={data.minPoints || 0}
              onChange={e => updateData('minPoints', parseInt(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              min="0"
            />
          </div>
        )}

        {condition.conditionType === 'level' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Minimum Level</label>
            <input
              type="number"
              value={data.minLevel || 1}
              onChange={e => updateData('minLevel', parseInt(e.target.value) || 1)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              min="1"
            />
          </div>
        )}

        {condition.conditionType === 'event_complete' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Event</label>
            <select
              value={data.eventId || ''}
              onChange={e => updateData('eventId', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select event...</option>
              {events.map(e => (
                <option key={e.id} value={e.id}>{e.title}</option>
              ))}
            </select>
          </div>
        )}

        {condition.conditionType === 'events_count' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Required Events</label>
            <input
              type="number"
              value={data.requiredCount || 1}
              onChange={e => updateData('requiredCount', parseInt(e.target.value) || 1)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              min="1"
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Description (shown to users)</label>
          <input
            type="text"
            value={condition.description || ''}
            onChange={e => updateCondition(index, 'description', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="e.g., Complete 5 museum challenges"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? t('cms.rewards.editReward') : t('cms.rewards.addReward')}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">

          {/* Basic Info */}
          <Section id="basic" title="Basic Info">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cms.common.name')} *</label>
              <input type="text" value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                placeholder="Reward name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={form.rewardType}
                  onChange={e => handleChange('rewardType', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  {REWARD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select value={form.difficulty}
                  onChange={e => handleChange('difficulty', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="">None</option>
                  {DIFFICULTY_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
              <input type="text" value={form.shortDescription}
                onChange={e => handleChange('shortDescription', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Brief description for cards" maxLength="500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cms.common.description')}</label>
              <textarea value={form.description}
                onChange={e => handleChange('description', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Detailed description..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input type="text" value={form.category}
                  onChange={e => handleChange('category', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g., Explorer, Collector" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cms.common.points')}</label>
                <input type="number" value={form.pointsValue}
                  onChange={e => handleChange('pointsValue', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  min="0" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
              <input type="text" value={form.imageUrl}
                onChange={e => handleChange('imageUrl', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="https://..." />
            </div>
          </Section>

          {/* Conditions */}
          <Section id="conditions" title={`Conditions (${conditions.length})`}>
            <p className="text-sm text-gray-500 mb-3">
              Conditions in the same group are OR'd (any one). Different groups are AND'd (all required).
            </p>
            <div className="space-y-3">
              {conditions.map((cond, idx) => (
                <ConditionEditor key={idx} condition={cond} index={idx} />
              ))}
            </div>
            <button type="button" onClick={addCondition}
              className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center gap-2">
              <FiPlus className="w-4 h-4" /> Add Condition
            </button>
          </Section>

          {/* Settings */}
          <Section id="settings" title="Behavior Settings">
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.isActive}
                  onChange={e => handleChange('isActive', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600" />
                <span className="text-sm">Active (visible to users)</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.autoClaim}
                  onChange={e => handleChange('autoClaim', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600" />
                <span className="text-sm">Auto-claim when conditions met</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.showProgress}
                  onChange={e => handleChange('showProgress', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600" />
                <span className="text-sm">Show progress to users</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.isSecret}
                  onChange={e => handleChange('isSecret', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600" />
                <span className="text-sm">Secret (hidden until unlocked)</span>
              </label>
            </div>
          </Section>

          {/* Limits */}
          <Section id="limits" title="Limits & Availability">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input type="datetime-local" value={form.startDate}
                  onChange={e => handleChange('startDate', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input type="datetime-local" value={form.endDate}
                  onChange={e => handleChange('endDate', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Total Claims</label>
                <input type="number" value={form.maxClaims}
                  onChange={e => handleChange('maxClaims', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Unlimited" min="1" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Per User</label>
                <input type="number" value={form.maxPerUser}
                  onChange={e => handleChange('maxPerUser', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  min="1" />
              </div>
            </div>
          </Section>

          {/* Translations */}
          <Section id="translations" title={
            <span className="flex items-center gap-2">
              <FiGlobe className="w-4 h-4" /> Translations
              {languages.length > 1 && (
                <span className="text-xs text-gray-400 font-normal">
                  ({Object.keys(translations).filter(l => l !== 'en' && (translations[l]?.name || translations[l]?.description)).length} languages)
                </span>
              )}
            </span>
          }>
            <LanguageTabsEditor
              languages={languages}
              defaultLang="en"
              translations={translations}
              fields={[
                { key: 'name', label: 'Name', type: 'input', placeholder: 'Reward name' },
                { key: 'shortDescription', label: 'Short Description', type: 'input', placeholder: 'Brief description for cards' },
                { key: 'description', label: 'Full Description', type: 'textarea', rows: 3, placeholder: 'Detailed description...' }
              ]}
              onChange={(locale, field, value) => {
                // For English, use handleChange to keep form in sync
                if (locale === 'en') {
                  handleChange(field, value);
                } else {
                  // For other languages, just update translations
                  setTranslations(prev => ({
                    ...prev,
                    [locale]: { ...prev[locale], [field]: value }
                  }));
                }
              }}
            />
            <p className="text-xs text-gray-400 mt-2">
              Edit English (default) content above or in Basic Info. Add translations for other languages here.
            </p>
          </Section>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            {t('cms.common.cancel')}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50">
            {saving ? 'Saving...' : t('cms.common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Rewards;
